<script setup lang="ts">
import {
  ref, defineProps, computed, watch, onMounted, defineEmits, defineExpose,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import draggable from 'vuedraggable';

import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '../../../../../assets/styles/qill.scss';
import { ElTimePicker } from 'element-plus';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { timezones } from '@/data/timezones';
import { combineDateAndTime } from '@/shared/sticker-date-combos';
import store from '@/store';
import GlowPicker from '@/components/StickerTIcket/GlowPicker.vue';

dayjs.extend(utc);
dayjs.extend(timezone);

const emit = defineEmits(['onFetchCauses']);

const props = defineProps<{
  causes: string[];
  availableTags: string[];
  causeLoading: boolean;
}>();

const allowNamingPrices = ref(false);

const dateStart = ref();
const timeStart = ref();
const dateEnd = ref();
const timeEnd = ref();
const dateTimeTimezone = ref();

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

const combineTime = () => {
  // Validate that all required fields are present before processing
  if (!dateStart.value || !timeStart.value || !dateEnd.value || !timeEnd.value || !dateTimeTimezone.value) {
    return; // Don't process if any required field is missing
  }

  const startDate = combineDateAndTime(dateStart.value, timeStart.value);
  const endDate = combineDateAndTime(dateEnd.value, timeEnd.value);
  const targetTimeZone = dateTimeTimezone.value;

  const convertToUTC = (date, timeZone) => {
    const localDate = dayjs.tz(date, timeZone);
    return localDate.utc().format();
  };

  if (targetTimeZone != null) {
    const startDateInUTC = convertToUTC(startDate, targetTimeZone);
    const endDateInUTC = convertToUTC(endDate, targetTimeZone);

    store.dispatch('sticker/SET_STICKER', {
      startDate: startDateInUTC,
      endDate: endDateInUTC,
      startDateTimeZone: targetTimeZone,
      endDateTimeZone: targetTimeZone,
    });
  }
};

// Validation function to check all required fields
const validateDateTime = () => {
  const errors = [];

  if (!item.value.title || item.value.title.trim() === '') {
    errors.push('Sticker Title');
  }

  if (!item.value.tags || item.value.tags.length === 0) {
    errors.push('Tags');
  }

  if (!item.value.description || item.value.description.replace(/<[^>]*>/g, '').trim() === '') {
    errors.push('Details');
  }

  if (!item.value.causes || item.value.causes.length === 0) {
    errors.push('Non-Profit Suggestions');
  }

  if (!dateStart.value) {
    errors.push('Start Date');
  }

  if (!timeStart.value) {
    errors.push('Start Time');
  }

  if (!dateEnd.value) {
    errors.push('End Date');
  }

  if (!timeEnd.value) {
    errors.push('End Time');
  }

  if (!dateTimeTimezone.value) {
    errors.push('Timezone');
  }

  return errors;
};

watch([dateStart, timeStart, dateEnd, timeEnd, dateTimeTimezone], () => {
  combineTime();
});

watch(() => [...(item.value.quickCosts || [])], (newQuickCosts) => {
  const hasQuickCosts = newQuickCosts.some((cost) => cost !== null && cost !== undefined && cost !== '' && cost !== 0);

  if (hasQuickCosts && allowNamingPrices.value) {
    item.value.cost = '0';
  }
}, { deep: true });

watch(allowNamingPrices, (newValue) => {
  if (!item.value.quickCosts) {
    item.value.quickCosts = newValue ? [0, 0, 0, 0] : [null, null, null, null];
  }

  if (newValue) {
    item.value.cost = '0';
  } else {
    for (let i = 0; i < item.value.quickCosts.length; i++) {
      item.value.quickCosts[i] = null;
    }
  }
});

onMounted(async () => {
  if (item?.value?.quickCosts?.length) {
    if (item.value.quickCosts.length !== 4) {
      const newArray = Array(4).fill(null);

      for (let i = 0; i < Math.min(item.value.quickCosts.length, 4); i++) {
        newArray[i] = item.value.quickCosts[i];
      }

      item.value.quickCosts = newArray;
    }

    const hasValidQuickCost = item.value.quickCosts.some((cost) => cost !== null && cost !== undefined && cost !== '' && cost !== 0);
    allowNamingPrices.value = hasValidQuickCost;

    if (!hasValidQuickCost && item.value.quickCosts) {
      for (let i = 0; i < item.value.quickCosts.length; i++) {
        item.value.quickCosts[i] = null;
      }
    }
  } else {
    if (!item.value.quickCosts) {
      item.value.quickCosts = [null, null, null, null];
    }
    allowNamingPrices.value = false;
  }

  const receivedStartDate = item.value.startDate;
  const timezone = item.value.startDateTimeZone;

  dateTimeTimezone.value = timezone;

  const start = dayjs.utc(receivedStartDate).tz(timezone);

  if (start && start.isValid()) {
    dateStart.value = start.format('YYYY-MM-DD');
    timeStart.value = start.format('hh:mm A');
  }

  const receivedEndDate = item.value.endDate;
  const end = dayjs.utc(receivedEndDate).tz(timezone);

  if (end && end.isValid()) {
    dateEnd.value = end.format('YYYY-MM-DD');
    timeEnd.value = end.format('hh:mm A');
  }
});

const prepareForSave = () => {
  // First ensure all required date/time fields are properly set via combineTime
  combineTime();

  if (!item.value.quickCosts) {
    item.value.quickCosts = allowNamingPrices.value ? [0, 0, 0, 0] : [null, null, null, null];
  }

  if (!Array.isArray(item.value.quickCosts) || item.value.quickCosts.length !== 4) {
    const newArray = Array(4).fill(null);

    if (Array.isArray(item.value.quickCosts)) {
      for (let i = 0; i < Math.min(item.value.quickCosts.length, 4); i++) {
        newArray[i] = item.value.quickCosts[i];
      }
    }

    item.value.quickCosts = newArray;
  }

  const hasQuickCosts = item.value.quickCosts.some((cost) => cost !== null && cost !== undefined && cost !== '' && cost !== 0);

  if (allowNamingPrices.value && hasQuickCosts) {
    item.value.cost = '0';

    for (let i = 0; i < item.value.quickCosts.length; i++) {
      if (item.value.quickCosts[i] !== null && item.value.quickCosts[i] !== undefined
          && item.value.quickCosts[i] !== '') {
        item.value.quickCosts[i] = Number(item.value.quickCosts[i]);

        if (isNaN(item.value.quickCosts[i]) || item.value.quickCosts[i] === 0) {
          item.value.quickCosts[i] = null;
        }
      } else {
        item.value.quickCosts[i] = null;
      }
    }
  } else if (!allowNamingPrices.value) {
    if (!item.value.cost || item.value.cost === '') {
      item.value.cost = '0';
    }

    for (let i = 0; i < item.value.quickCosts.length; i++) {
      item.value.quickCosts[i] = null;
    }
  }
};

defineExpose({ prepareForSave, validateDateTime });

const preventArrowKeys = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
  }
};

// Slug logic
const sanitizeSlug = () => {
  item.value.slug = item.value.slug.replace(/[^a-z0-9-]/g, '').toLowerCase();
};
const slugTip = computed(() => 'A slug is a URL-friendly version of your sticker name. It creates clean, memorable URLs like "spotlight.xyz/web/sticker/your-sticker-name". '
    + 'Slugs improve SEO, make sharing easier, and provide a professional appearance. Use lowercase letters, numbers, and hyphens only.');

// Draggable causes list - convert string array to objects for vuedraggable
const draggableCauses = computed({
  get: () => {
    if (!item.value.causes || !Array.isArray(item.value.causes)) return [];
    return item.value.causes.map((cause, index) => ({ id: index, name: cause }));
  },
  set: (value) => {
    item.value.causes = value.map((obj) => obj.name);
  },
});

// Remove cause from list
const removeCause = (causeName: string) => {
  if (item.value.causes && Array.isArray(item.value.causes)) {
    const index = item.value.causes.indexOf(causeName);
    if (index > -1) {
      item.value.causes.splice(index, 1);
    }
  }
};

</script>

<template>
  <section class="c-campaign-sticker">
    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">Sticker Title*</span>
      <input class="c-input" type="text" required placeholder="Title" v-model="item.title" />
    </article>
    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">
        Slug
        <el-tooltip
          effect="light"
          :content="slugTip"
          placement="top"
          popper-class="max-w-350"
        >
          <fai type="fal" icon="info-circle" class="size-18 ms-2" />
        </el-tooltip>
      </span>
      <input
        class="c-input"
        type="text"
        placeholder="your-sticker-name"
        v-model="item.slug"
        @input="sanitizeSlug"
      />
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--tags-cost">
      <div class="c-campaign-sticker__row c-campaign-sticker__row--column c-campaign-sticker__row--tags">
        <span class="c-campaign-sticker__label">Tags*</span>
        <vue-multiselect
          class="c-input-multiselect"
          v-model="item.tags"
          :multiple="true"
          :options="availableTags"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          placeholder="Tag"
        />
      </div>
      <div class="c-campaign-sticker__row c-campaign-sticker__row--column">
        <span class="c-campaign-sticker__label">Cost</span>
        <input class="c-input" type="text" required placeholder="Cost" v-model="item.cost" :disabled="allowNamingPrices" />
      </div>
    </article>

    <article class="c-quick-costs">
      <el-checkbox v-model="allowNamingPrices">Allow user's to name price</el-checkbox>

      <div v-if="allowNamingPrices" class="c-quick-costs__container">
        <p class="c-quick-costs__title">Quick cost options</p>
        <div class="c-quick-costs__costs">
          <section class="position-relative" v-for="(cost, index) in 4">
            <div class="c-input-prefix" v-show="item.quickCosts[index]">$</div>
            <input
              class="c-input"
              :key="cost"
              v-model.number="item.quickCosts[index]"
              size="10"
              :placeholder="'Cost ' + (cost)"
              type="number"
              step="1"
              min="0"
              @keydown="preventArrowKeys"
              @wheel.prevent
            />
          </section>
        </div>
      </div>
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">Details*</span>
      <QuillEditor :toolbar="['bold', 'italic', 'underline', 'strike']" placeholder="" v-model:content="item.description" content-type="html" />
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--column c-campaign-sticker__row--causes">
      <span class="c-campaign-sticker__label">Non-Profit Suggestions*</span>
      <vue-multiselect
        class="c-input-multiselect"
        :multiple="true"
        :options="causes"
        :searchable="true"
        :internal-search="false"
        :clear-on-select="false"
        :close-on-select="true"
        :show-labels="false"
        :preserve-search="true"
        placeholder="Non profit"
        v-model="item.causes"
        :loading="causeLoading"
        @search-change="(val) => emit('onFetchCauses', val)"
      />

      <div v-if="item.causes && item.causes.length > 0" class="causes-draggable-list">
        <draggable
          v-model="draggableCauses"
          item-key="id"
          handle=".drag-handle"
        >
          <template #item="{ element }">
            <div class="cause-item">
              <fai type="fal" icon="grip-vertical" class="drag-handle size-16" />
              <span class="cause-name">{{ element.name }}</span>
              <fai type="fal" icon="times" class="remove-btn size-16" @click="removeCause(element.name)" />
            </div>
          </template>
        </draggable>
      </div>
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">Max Quantity (optional)</span>
      <input class="c-input" type="number" v-model="item.maxQuantity" @keydown="preventArrowKeys" @wheel.prevent />
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">Start Date & Time*</span>
      <article class="c-glow-picker__group">
        <div class="c-glow-picker__group__inner">
          <el-date-picker class="c-glow-picker__input" v-model="dateStart" format="MM/DD/YY" />
          <div class="position-relative">
            <el-time-picker
              arrow-control
              class="c-custom-time"
              format="hh:mm A"
              v-model="timeStart"
              value-format="hh:mm A"
            />
          </div>
        </div>
      </article>
    </article>

    <article class="c-campaign-sticker__row c-campaign-sticker__row--column">
      <span class="c-campaign-sticker__label">End Date & Time*</span>
      <article class="c-glow-picker__group">
        <div class="c-glow-picker__group__inner">
          <el-date-picker class="c-glow-picker__input" v-model="dateEnd" format="MM/DD/YY" />
          <div class="position-relative">
            <el-time-picker
              arrow-control
              class="c-custom-time"
              format="hh:mm A"
              v-model="timeEnd"
              value-format="hh:mm A"
            />
          </div>
        </div>
      </article>
    </article>

    <el-select class="c-glow-picker__input" placeholder="Timezone" v-model="dateTimeTimezone">
      <el-option
        v-for="timezone in timezones"
        :key="timezone.value"
        :label="timezone.label"
        :value="timezone.value" />
    </el-select>

    <!-- <glow-picker /> -->
  </section>

</template>

<style lang="scss">
.c-campaign-sticker {
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-width: 340px;

  .el-select {
    width: 100%;
    max-width: 340px;

    &__placeholder {
      color: #000000;
      font-family: "Red Hat Display";
      font-size: 14px;
      font-weight: 500;

      &.is-transparent {
        color: #606266;
        font-size: 0.875rem;
        font-weight: 400;
      }
    }

    &__wrapper {
      height: 100%;
      border: none;
      box-shadow: none;
      width: 338px;
    }
  }

  &__label {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: 500;
  }

  &__row {
    display: flex;

    &--tags {
      .multiselect {
        max-width: 160px;
      }
    }

    &--causes {
      .multiselect {
        max-width: 340px;
      }
    }

    &--column {
      flex-direction: column;
    }

    & {
      position: relative;
    }

    &--tags-cost {
      gap: 20px;
      > div {
        width: calc(50% - 10px);
      }
    }
  }

  /* Hide the arrows in number input fields */
  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield; /* Firefox */
    appearance: textfield; /* Standard property for compatibility */
  }
}

.causes-draggable-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cause-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f5;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .drag-handle {
    cursor: move;
    color: #adb5bd;
    flex-shrink: 0;

    &:hover {
      color: #6c757d;
    }
  }

  .cause-name {
    flex: 1;
    font-size: 14px;
    color: #212529;
    font-weight: 500;
  }

  .remove-btn {
    cursor: pointer;
    color: #adb5bd;
    flex-shrink: 0;
    transition: color 0.2s ease;

    &:hover {
      color: #dc3545;
    }
  }
}

// Dragging state styles
.sortable-ghost {
  opacity: 0.5;
  background: #e7f3ff;
}

.sortable-drag {
  opacity: 0.8;
  cursor: move;
}
</style>
